/**
 * /registrer?next=demo, the site's «Åpne gratisdemo» (approved design 2026-10-12,
 * docs/marketing/handoff-design-2026-10-12/DEMO-ENTRY.md). The address field is on the page, not
 * behind a window. The request is the one the demo form always made: POST /account/demo-now. A new
 * address answers with a session and the demo opens; a known one answers the same neutral 202 as the
 * mailed link, and the page says to check the mail without saying whether an account exists.
 * Styles are scoped under #timefly-demo-entry (demo-entry.css), so no other account page changes.
 */
import { ApiError, post } from '@/api/client';
import { forgetDestination } from '@/auth/destination';
import { type SessionUser, useAuth } from '@/auth/store';
import { API_BASE } from '@/config';
import { T } from '@/i18n';
import { recordPublicPage, track } from '@/marketing/analytics';
import { countStep } from '@/marketing/count';
import { completeRegistration, registrationPageView } from '@/marketing/pixel';
import { commitGeneration } from '@/shell/context';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LOGO, PROVIDER_NAME, SIGN_IN_FAILED, useOnePress } from './providers';
import { demoHome, failureText, seedFromQuery, useAvailability } from './shared';
import './demo-entry.css';

const SITE = 'https://timefly.no/';
const NO_DEMO = 'Vi fikk ikke åpnet demoen. Prøv igjen. E-postadressen er beholdt.';

type DemoAnswer =
  | { token?: unknown; role?: unknown; user?: unknown; ok?: unknown; demo?: unknown }
  | null
  | undefined;
/**
 * What POST /account/demo-now answered, read before anything acts on it: a session (a new address,
 * accountController.js demoNow), the neutral { ok: true } of the mailed link (demoLink, a known or
 * ambiguous address), or neither. Neither is not «check your mail»: no mail was sent for it.
 */
export function demoOutcome(
  r: DemoAnswer,
):
  | { kind: 'session'; token: string; role: string | null; user: SessionUser }
  | { kind: 'mail' }
  | { kind: 'broken' } {
  const user = r?.user as SessionUser | undefined;
  if (typeof r?.token === 'string' && r.token && typeof user?.id === 'number')
    return { kind: 'session', token: r.token, role: typeof r.role === 'string' ? r.role : null, user };
  if (r?.ok === true && !r.token && !r.user) return { kind: 'mail' };
  return { kind: 'broken' };
}

export default function DemoEntry() {
  const available = useAvailability();
  const { search } = useLocation();
  const nav = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const q = new URLSearchParams(search);
  // where the visit came from, as the site put it in the link: the lead keeps it (leads.came_from)
  const utm = q.get('utm_source') ?? '';
  const utm_content = q.get('utm_content') ?? ''; // which ad, 2026-10-04
  const seed = seedFromQuery(search); // «?demo=bygg»: the trade firm (#128)
  // back from Google, Microsoft or Apple without getting in (accountController.js oauthCallback)
  const signInFailed = q.get('innlogging');
  const [email, setEmail] = useState('');
  // the address the request carried, as the server reads it (accountService normEmail): the mail step
  // names this one, not whatever the field holds by the time the answer comes
  const [submitted, setSubmitted] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  // an error by the field: `field` marks the address as the problem (aria-invalid)
  const [err, setErr] = useState<{ text: string; field: boolean } | null>(
    signInFailed
      ? {
          text: T(SIGN_IN_FAILED[signInFailed] ?? 'Innloggingen gikk ikke. Prøv igjen, eller bruk e-post.'),
          field: false,
        }
      : null,
  );
  const input = useRef<HTMLInputElement>(null);
  const mailTitle = useRef<HTMLHeadingElement>(null);

  // the registration page is one of the two places the Meta Pixel may run, with marketing consent
  useEffect(() => registrationPageView(), []);
  // replay of this public page, inputs masked (owner 2026-10-05)
  useEffect(() => recordPublicPage(), []);
  // counted for every visitor, no cookie and nothing about the person (marketing/count.ts)
  useEffect(() => countStep('form_view'), []);
  // the first real interaction, once (issue #15): not every keystroke
  const startedRef = useRef(false);
  const started = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    countStep('form_typed');
    track('signup_started', { signup_method: 'email' });
  };
  // the mail step takes focus on its heading; «Endre e-postadresse» gives it back to the field
  const wasSent = useRef(false);
  useEffect(() => {
    if (sent) mailTitle.current?.focus();
    else if (wasSent.current) input.current?.focus();
    wasSent.current = sent;
  }, [sent]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busyRef.current) return; // the button stays focusable while busy, so a second press lands here
    if (!input.current?.validity.valid || !email.trim()) {
      setErr({ text: T('Skriv inn en gyldig e-postadresse.'), field: true });
      input.current?.focus();
      return;
    }
    const address = email.trim().toLowerCase();
    busyRef.current = true;
    setBusy(true);
    setErr(null);
    track('signin_method_chosen', { method: 'email', page: 'signup' });
    try {
      const r = await post<DemoAnswer>('/account/demo-now', { email: address, utm, utm_content, ...seed });
      const out = demoOutcome(r);
      if (out.kind === 'broken') {
        setErr({ text: T(NO_DEMO), field: false });
        return;
      }
      completeRegistration(); // the ad conversion, in the browser the ad was clicked in (pixel.ts)
      if (out.kind === 'session') {
        setSession(out.token, { ...out.user, role: out.role });
        commitGeneration(out.user.id, null, null); // the token names the demo; nothing from before is readable
        forgetDestination();
        nav(demoHome({ new_demo: true, demo: r?.demo }), { replace: true });
        return;
      }
      setSubmitted(address);
      setSent(true);
    } catch (ex) {
      // the address stays in the field; no answer at all is said as the design's connection error
      setErr({
        text: ex instanceof ApiError ? failureText(ex) : T(NO_DEMO),
        field: ex instanceof ApiError && ex.code === 'email_invalid',
      });
      if (ex instanceof ApiError && ex.code === 'email_invalid') input.current?.focus();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  const providers = available.signInWith;
  const closed = available.signup === false;

  return (
    <div id="timefly-demo-entry">
      <div className="entry-page">
        <header className="entry-header">
          <a href={SITE} aria-label={T('Tilbake til Timefly')}>
            <img src="/brand/timefly-wordmark-blue-400.png" width="132" height="39" alt="Timefly" />
          </a>
          <a className="back-link" href={SITE}>
            {T('Tilbake til nettsiden')}
          </a>
        </header>
        <main className="entry-layout">
          <section className="entry-form-area" aria-labelledby={sent ? 'entry-mail-title' : 'entry-title'}>
            {closed ? (
              <div>
                <h1 id="entry-title">{T('Åpne gratisdemoen.')}</h1>
                <output className="entry-info">{T('Registrering er ikke åpnet ennå.')}</output>
                <p className="entry-login">
                  {T('Bruker du Timefly fra før?')} <Link to="/login">{T('Logg inn')}</Link>
                </p>
              </div>
            ) : sent ? (
              <section>
                <h1 id="entry-mail-title" ref={mailTitle} tabIndex={-1}>
                  {T('Sjekk e-posten din.')}
                </h1>
                <p className="entry-intro">
                  {T('Hvis adressen kan brukes, sender vi deg en lenke for å åpne Timefly.')}
                </p>
                <div className="mail-destination">
                  <span>{T('E-postadresse')}</span>
                  <strong className="ph-no-capture">{submitted}</strong>
                </div>
                <p className="mail-help">
                  {T('Lenken er gyldig i 30 minutter. Finner du den ikke, sjekk søppelpost.')}
                </p>
                <button type="button" className="entry-secondary" onClick={() => setSent(false)}>
                  {T('Endre e-postadresse')}
                </button>
                <p className="entry-login">
                  {T('Har du passord?')} <Link to="/login">{T('Logg inn i stedet')}</Link>
                </p>
              </section>
            ) : (
              <div>
                <h1 id="entry-title">{T('Åpne gratisdemoen.')}</h1>
                <p className="entry-intro">
                  {seed.demo
                    ? T(
                        'Fire uker med timer på oppdrag venter. Prøv Timefly med oppdiktede ansatte og kunder.',
                      )
                    : T('En ferdig vaktplan venter. Prøv Timefly med oppdiktede ansatte og vakter.')}
                </p>
                <form noValidate onSubmit={submit} onFocus={started}>
                  <label className="field-label" htmlFor="entry-email">
                    {T('E-post')}
                  </label>
                  <input
                    ref={input}
                    id="entry-email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder={T('deg@bedrift.no')}
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    readOnly={busy}
                    aria-invalid={err?.field ? true : undefined}
                    aria-describedby={err ? 'entry-email-help entry-error' : 'entry-email-help'}
                  />
                  <p id="entry-email-help" className="field-help">
                    {T('Du trenger ikke velge passord.')}
                  </p>
                  {err && (
                    <p id="entry-error" className="entry-error" role="alert">
                      {err.text}
                    </p>
                  )}
                  <button
                    className="entry-primary"
                    type="submit"
                    aria-disabled={busy ? true : undefined}
                    data-testid="entry-submit"
                  >
                    <span>{busy ? T('Åpner demobedriften …') : T('Åpne gratisdemo')}</span>
                  </button>
                  <output className="entry-sr" aria-live="polite">
                    {busy ? T('Åpner demobedriften …') : ''}
                  </output>
                  <p className="no-card">{T('Gratis å prøve, ingen kort')}</p>
                </form>
                <Providers
                  list={providers}
                  loading={available.loading}
                  failed={available.unknown}
                  retry={available.retry}
                />
                <p className="privacy-note">
                  {T(
                    'Vi bruker e-postadressen til demoen og til å tilby hjelp. Demobedriften er tilgjengelig i 30 dager.',
                  )}
                </p>
                <details className="entry-details">
                  <summary>{T('Hva skjer når jeg fortsetter?')}</summary>
                  <p>
                    {T(
                      'Med en ny e-postadresse åpnes demobedriften. Hvis det trengs bekreftelse, får du en lenke på e-post. Egne ansatte og bedriftsopplysninger legger du inn først når du vil ta Timefly i bruk.',
                    )}
                  </p>
                </details>
                <p className="entry-login">
                  {T('Bruker du Timefly fra før?')} <Link to="/login">{T('Logg inn')}</Link>
                </p>
              </div>
            )}
            <footer className="entry-footer">
              <a href="https://timefly.no/hjelp/" target="_blank" rel="noreferrer">
                {T('Hjelp')}
              </a>
              <a href="https://timefly.no/privacy/">{T('Personvern')}</a>
            </footer>
          </section>
          <Preview />
        </main>
      </div>
    </div>
  );
}

/** Google, Microsoft, Apple: only the ones the server says are on, while it is read, or a retry when the read failed. */
function Providers({
  list,
  loading,
  failed,
  retry,
}: { list: string[]; loading: boolean; failed: boolean; retry: () => void }) {
  const { opening, press } = useOnePress('signup');
  if (loading) return <p className="provider-state">{T('Henter innloggingsvalg …')}</p>;
  if (failed)
    return (
      <output className="provider-state">
        {T('Fikk ikke hentet andre måter å logge inn på.')}{' '}
        <button type="button" onClick={retry}>
          {T('Prøv igjen')}
        </button>
      </output>
    );
  if (!list.length) return null;
  return (
    <>
      <div className="entry-divider">
        <span>{T('eller fortsett med')}</span>
      </div>
      <fieldset className={`provider-options n${Math.min(list.length, 3)}`}>
        <legend className="entry-sr">{T('Andre måter å fortsette på')}</legend>
        {list.map((p) => {
          const name = PROVIDER_NAME[p] ?? p;
          return (
            <a
              key={p}
              className="provider"
              href={`${API_BASE}/account/oauth/${p}`}
              data-testid={`sign-in-${p}`}
              aria-label={opening === p ? T('Åpner {name} …', { name }) : T('Fortsett med {name}', { name })}
              aria-disabled={opening != null || undefined}
              onClick={(e) => press(p, e)}
            >
              {LOGO[p]}
              {opening === p ? T('Åpner {name} …', { name }) : name}
            </a>
          );
        })}
      </fieldset>
    </>
  );
}

type Cell = [string, string, string] | null;
const ROWS: [string, Cell, Cell, Cell][] = [
  ['Nora', ['07–15', 'Dag', 'day'], null, ['07–15', 'Dag', 'day']],
  ['Emil', ['10–18', 'Ekstra', 'extra'], ['15–23', 'Kveld', 'evening'], null],
  ['Ida', ['15–23', 'Kveld', 'evening'], ['15–23', 'Kveld', 'evening'], null],
];

/** The left panel on wide screens: an illustration with invented people, not the visitor's demo. */
function Preview() {
  return (
    <aside className="entry-preview" aria-labelledby="preview-title">
      <h2 id="preview-title">
        {T('Alt er klart.')}
        <br />
        {T('Prøv deg frem.')}
      </h2>
      <p>{T('Endre en vakt, se varsler eller be AI-assistenten lage neste ukes plan.')}</p>
      <div className="preview-plan">
        <div className="preview-plan-top">
          <strong>{T('Vaktplan')}</strong>
          <span>Fjordlys Kafé, demo</span>
        </div>
        <table aria-label={T('Illustrasjon av en vaktplan med oppdiktede ansatte')}>
          <thead>
            <tr>
              <th scope="col">{T('Ansatt')}</th>
              <th scope="col">
                {T('Man')} <span>12</span>
              </th>
              <th scope="col">
                {T('Tir')} <span>13</span>
              </th>
              <th scope="col">
                {T('Ons')} <span>14</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([who, ...cells]) => (
              <tr key={who}>
                <th scope="row">{who}</th>
                {cells.map((c, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: a fixed three-day row
                  <td key={i}>
                    {c ? (
                      <div className={`preview-shift ${c[2]}`}>
                        <strong>{c[0]}</strong>
                        <span>{T(c[1])}</span>
                      </div>
                    ) : (
                      <span className="free">{T('Fri')}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="preview-assistant">
          <span className="assistant-mark" aria-hidden="true">
            AI
          </span>
          <span>«{T('Lag neste ukes vaktplan.')}»</span>
        </div>
      </div>
      <p className="preview-caption">{T('Eksempelvisning med oppdiktede ansatte.')}</p>
      <p className="preview-close">{T('Utforsk først. Sett opp din egen bedrift når du er klar.')}</p>
    </aside>
  );
}
